<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title">
            Notifications
          </div>
        </template>
      </EsHeader>
    </div>
    <es-loader v-if="loading" style="margin: 5rem"/>
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="0"
      style="padding: 5rem"
    />
    <div v-else class="content">
      <es-form style="padding: 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div>
              <th>
              <font-awesome-icon :icon="['fas', 'cog']" size="lg"/>
                Add Near Match Notification
              </th>
            </div>
          </div>
        </template>
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <th class="has-text-grey no-border top-padding table-header-normal">
                  Job Type
                </th>
                <td class="no-border top-padding">
                  <div class="control">
                    <input
                      class="input no-border"
                      type="text"
                      value="Near Match"
                      aria-label="Near Match"
                      readonly
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <th
                  class="has-text-grey no-border table-header-normal">
                  Job Trigger Type
                </th>
                <td class="no-border">
                  <div class="control">
                    <input
                      class="input no-border"
                      type="text"
                      value="Pending Near Match"
                      aria-label="Pending Near Match"
                      readonly
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <th
                  class="has-text-grey no-border table-header-normal"
                >
                  Job Level
                </th>
                <td class="no-border">
                  <div class="control">
                    <div
                      class="buttons has-addons"
                    >
                      <button
                        class="button button--form"
                        v-for="option in eventOptions"
                        :key="option.description"
                        @click="jobLevel = option.description"
                        :class="{ 'is-active': jobLevel === option.description }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
              <tr v-if="jobLevel === 'State'">
                <th class="has-text-grey no-border table-header-normal">
                  Send To
                </th>
                <td class="control no-border">
                  <span>State Super Users</span>
                </td>
              </tr>
              <tr v-else>
                <th class="has-text-grey no-border table-header-normal">
                  Send To
                </th>
                <td class="no-border">
                  <div class="control buttons has-addons">
                    <button
                      class="button button--form"
                      v-for="option in filteredSendToOptions"
                      :key="option.description"
                      @click.prevent="sendToType = option.description"
                      :class="{ 'is-active': sendToType == option.description }"
                    >
                      {{ option.description }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <th class="has-text-grey no-border top-padding table-header-normal">
                  Additional Email (optional)
                </th>
                <td class="no-border">
                  <div class="control">
                    <input
                      class="input has-text-grey is-italic"
                      type="text"
                      maxlength="250"
                      v-model="additionalEmail"
                      :aria-label="additionalEmail || 'Additional Email'"
                      placeholder="sample@email.com"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <th class="has-text-grey no-border top-padding table-header-normal">
                  Email Template
                </th>
                <td class="no-border">
                  <div class="control">
                    <div
                      class="
                        is-flex
                        flex-direction--column
                        flex-justify--start
                        file
                        has-name
                        is-right
                        is-fullwidth
                      "
                    >
                      <es-email-template-upload-input
                        input-id="id"
                        input-ref="inputFile"
                        @validFileExtensions="setErrors($event)"
                        :has-error-file="hasErrorFile"
                        :selected-files="files"
                      />
                      <div v-if="hasErrorFile" class="help is-danger">
                        {{ errorMsgFile }}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <th class="has-text-grey no-border top-padding table-header-normal">
                  Frequency Type
                </th>
                <td class="no-border">
                  <div class="control buttons has-addons">
                    <button
                      class="button button--form"
                      v-for="option in frequencyOptions"
                      :key="option.description"
                      @click.prevent="selectFrequencyType(option)"
                      :class="{ 'is-active': frequencyType === option.description }"
                    >
                      {{ option.description }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="frequencyType !== 'Daily'">
                <th class="has-text-grey no-border top-padding table-header-normal">
                  Frequency Details
                </th>
                <td class="no-border">
                  <div class="control buttons has-addons" v-if="frequencyType === 'Weekly'">
                    <button
                      class="button button--form"
                      v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']"
                      :key="day"
                      @click="updateWeeklyFrequency(day)"
                      :class="{ 'is-active': day === weeklyFrequencyDetail }"
                    >
                     {{ day }}
                    </button>
                  </div>
                  <div
                    v-if="frequencyType === 'Monthly'"
                    class="select is-half-width is-pulled-left"
                  >
                    <select
                      class="input"
                      v-model="monthlyFrequencyDetail"
                      title="Frequency Details"
                    >
                      <option
                        v-for="(frequency, index) in getFrequencyDetailOptions()"
                        :key="index"
                        :value="frequency.value"
                      >
                        {{ frequency.label }}
                      </option>
                    </select>
                  </div>
                </td>
              </tr>
              <tr>
                <th class="has-text-grey no-border top-padding table-header-normal">
                  Start Time
                </th>
                <td class="no-border selections">
                  <div class="select">
                    <select class="input" v-model="hourOption" aria-label="Select Hour">
                      <option
                        v-for="(hour, index) in getHourOptions()"
                        :key="index"
                        :value="hour"
                      >
                        {{ hour }}
                      </option>
                    </select>
                  </div>
                  <strong style="margin: 0 5px;">:</strong>
                  <div class="select">
                    <select class="input" v-model="minuteOption" aria-label="Select Minute">
                      <option
                        v-for="(minute, index) in getMinuteOptions()"
                        :key="index"
                        :value="minute"
                      >
                        {{ minute }}
                      </option>
                    </select>
                  </div>
                  <div style="margin: 0 1rem;">
                    <button
                      class="button button--form"
                      v-for="option in amPmOptions"
                      :key="option.description"
                      @click="amPmType = option.description"
                      :class="{ 'is-active': amPmType == option.description }"
                    >
                      {{ option.description }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <th class="has-text-grey table-header-normal last-row">
                  Severity
                </th>
                <td class="no-border">
                  <div class="control buttons has-addons">
                    <button
                      class="button button--form"
                      v-for="option in severityOptions"
                      :key="option.description"
                      @click.prevent="severityData = option.description"
                      :class="{ 'is-active': severityData === option.description }"
                    >
                      {{ option.description }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
      <div
        class="is-flex flex-justify--end flex-align--center"
        style="margin-right: 2rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          @click.prevent="cancelPanel()"
          style="margin-right: 1rem"
        >
          Cancel
        </button>
        <button
          class="button is-primary"
          @click.prevent="checkRequiredFields"
        >
          Add Near Match Notification
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsEmailTemplateUploadInput from '@/components/EsEmailTemplateUploadInput.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleLeft,
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsEmailTemplateUploadInput,
  },
  watch: {
    hourOption() {
      this.calculateTime();
    },
    minuteOption() {
      this.calculateTime();
    },
    amPmType() {
      this.calculateTime();
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.severityOptions = [
      {
        description: 'High',
      },
      {
        description: 'Medium',
      },
      {
        description: 'Low',
      },
    ];
    this.eventOptions = [
      {
        description: 'State',
      },
      {
        description: 'District',
      },
      {
        description: 'School',
      },
    ];
    this.frequencyOptions = [
      {
        description: 'Daily',
      },
      {
        description: 'Weekly',
      },
      {
        description: 'Monthly',
      },
    ];
    this.amPmOptions = [
      {
        description: 'AM',
      },
      {
        description: 'PM',
      },
    ];
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
      activeModules: 'appModules/activeModules',
      allModules: 'appModules/getAllModules',
      properties: 'propertiesModule/properties',
    }),
    formDataBody() {
      const selectedTemplateName = this.files[0].name;
      const formData = new FormData();
      formData.append('jobTriggerType', 'Pending Near Match');
      formData.append('jobType', 'Near Match');
      formData.append('jobLevel', this.jobLevel);

      formData.append('severity', this.severityData);
      formData.append('sendTo', this.sendToType);
      formData.append('additionalEmail', this.additionalEmail);

      formData.append('template', selectedTemplateName);
      formData.append('templateContent', this.files[0]);

      formData.append('frequencyType', this.frequencyType);
      formData.append('frequencyDayOfWeek', this.weeklyFrequencyDetail);
      formData.append('frequencyDayOfMonth', this.monthlyFrequencyDetail);
      formData.append('startTime', this.startTime);

      if ((this.activeModule && this.activeModule.name) === 'personId') {
        formData.append('scheduleType', 'PID');
      } else if ((this.activeModule && this.activeModule.name) === 'directMatch') {
        formData.append('scheduleType', 'DM');
      } else if ((this.activeModule && this.activeModule.name) === 'locationID') {
        formData.append('scheduleType', 'LOC');
      }

      return formData;
    },
    filteredSendToOptions() {
      if (this.jobLevel === 'State') {
        return null;
      } if (this.jobLevel === 'District') {
        return [
          { description: 'Location Contact Person(s)' },
          { description: 'Location Administrator Users' },
        ];
      } if (this.jobLevel === 'School') {
        return [
          { description: 'Building Contact Person(s)' },
          { description: 'Building Administrator Users' },
        ];
      }
      return [];
    },
  },
  methods: {
    initialData() {
      return {
        loading: false,
        loadingError: false,
        hasErrorFile: false,
        errorMsgFile: null,

        monthlyFrequencyDetail: null,
        weeklyFrequencyDetail: null,

        hourOption: null,
        minuteOption: null,
        amPmType: 'AM',
        startTime: null,

        frequencyType: 'Monthly',
        jobLevel: 'State',
        files: [],
        jobType: 'Near Match',
        sendToType: '',
        severityData: 'Medium',
        additionalEmail: '',
      };
    },
    async addNotification() {
      this.clearErrors();
      this.loading = true;
      this.loadingError = false;
      try {
        await axios({
          method: 'post',
          url: 'schedules',
          data: this.formDataBody,
        });
        this.$emit('addNearMatchNotification');
        this.showNotification('success', ['The Near Match Notification has been added successfully.']);
      } catch (e) {
        this.loadingError = true;
        this.$emit('addNearMatchNotification');
        this.showNotification('danger', [e.message]);
      } finally {
        this.loading = false;
      }
    },
    checkRequiredFields() {
      if (this.files.length) {
        if (!this.hasErrorFile) {
          this.addNotification();
        } else {
          this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        }
      } else {
        this.hasErrorFile = true;
        this.errorMsgFile = 'No file chosen. A File is required to Upload.';
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      }
    },
    selectFrequencyType(option) {
      this.frequencyType = option.description;
      if (option.description !== 'Weekly') {
        this.weeklyFrequencyDetail = '';
      }
      if (option.description !== 'Monthly') {
        this.monthlyFrequencyDetail = '';
      }
    },
    addNearMatchNotification() {
      this.clearErrors();
    },
    calculateTime() {
      const hour = parseInt(this.hourOption, 10);
      const minute = parseInt(this.minuteOption, 10);
      let updatedHour = hour;
      if (this.amPmType === 'PM' && hour < 12) {
        updatedHour += 12;
      } else if (this.amPmType === 'AM' && hour === 12) {
        updatedHour = 0;
      }
      const time = updatedHour * 100 + minute;
      this.startTime = time;
    },
    updateWeeklyFrequency(day) {
      this.weeklyFrequencyDetail = day;
    },
    getFrequencyDetailOptions() {
      const options = [];
      for (let i = 1; i <= 31; i += 1) {
        let suffix = 'th';
        if (i === 1 || i === 21 || i === 31) {
          suffix = 'st';
        } else if (i === 2 || i === 22) {
          suffix = 'nd';
        } else if (i === 3 || i === 23) {
          suffix = 'rd';
        }
        options.push({
          value: i,
          label: `${i}${suffix}`,
        });
      }
      return options;
    },
    getHourOptions() {
      const options = [];
      for (let i = 1; i <= 12; i += 1) {
        options.push(i.toString());
      }
      return options;
    },
    getMinuteOptions() {
      const options = [];
      for (let i = 0; i <= 59; i += 1) {
        options.push(i.toString().padStart(2, '0'));
      }
      return options;
    },
    cancelPanel() {
      this.$emit('addNearMatchNotification');
    },
    clearErrors() {
      this.hideNotification();
      this.hasErrorFile = false;
      this.errorMsgFile = null;
    },
    setErrors(value) {
      if (!value) {
        this.hasErrorFile = true;
        this.errorMsgFile = 'File extension is invalid. Valid extensions are \'.txt\' or \'.csv\' or \'.TXT\' or \'.CSV\'';
      } else {
        this.clearErrors();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    vertical-align: middle
  table td
    vertical-align: middle
    padding-left: 0
    &:first-child
      width: 30%
  .header
    display: flex
    align-items: center
  .no-border
    border: 0
  .margin-right
    margin-right: 1.5rem
  .top-padding
    padding-top: 1rem
  .selections
    display: flex
    align-items: center
  .select:not
    right: 0em
</style>
